'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { CircleCheck, Eye, EyeOff, Loader2, Lock, LogIn, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLogin } from '@/hooks/useLogin';
import type { Sesion } from '@/lib/auth';

const TEXTO_ESTADO: Record<string, string> = {
  idle: 'Ingresar',
  validando: 'Validando credenciales…',
  verificando: 'Verificando acceso…',
  ok: '¡Acceso concedido!',
};

// Campos del diseño de referencia (Login.png) en verde institucional: etiquetas
// verdes seminegrita, inputs con borde de acento e ícono adentro, ojito +
// casilla "Mostrar contraseña" y botón degradado centrado.
const CLASE_INPUT =
  'h-12 rounded-xl border-2 border-primary/45 bg-white text-[15px] text-slate-900 shadow-sm transition-colors duration-200 placeholder:text-primary/45 hover:border-primary/70 focus-visible:border-primary focus-visible:ring-primary/20';

export default function LoginForm({
  onLogin,
  onRecuperar,
}: {
  onLogin: (s: Sesion) => void;
  onRecuperar: () => void;
}) {
  const { form, estado, error, onSubmit } = useLogin(onLogin);
  const [verClave, setVerClave] = useState(false);
  const ocupado = estado === 'validando' || estado === 'verificando' || estado === 'ok';
  const erroresCampo = form.formState.errors;

  const campo = (delay: number) => ({
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, delay, ease: 'easeOut' as const },
  });

  return (
    <>
      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        <motion.div {...campo(0.45)} className="space-y-2">
          <Label htmlFor="cedula" className="font-semibold text-primary">
            Número de Documento
          </Label>
          <div className="relative">
            <User className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-primary" />
            <Input
              id="cedula"
              type="text"
              inputMode="numeric"
              autoComplete="username"
              placeholder="Ej: 123456789"
              aria-invalid={!!erroresCampo.cedula}
              className={`${CLASE_INPUT} pl-11`}
              {...form.register('cedula')}
            />
          </div>
          {erroresCampo.cedula && (
            <p className="text-xs font-medium text-destructive">{erroresCampo.cedula.message}</p>
          )}
        </motion.div>

        <motion.div {...campo(0.53)} className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="clave" className="font-semibold text-primary">
              Contraseña
            </Label>
            <button
              type="button"
              className="text-xs font-medium text-primary underline-offset-4 transition-colors duration-150 hover:text-primary/80 hover:underline"
              onClick={onRecuperar}
            >
              Recuperar contraseña
            </button>
          </div>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-primary" />
            <Input
              id="clave"
              type={verClave ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Ingrese su contraseña"
              aria-invalid={!!erroresCampo.clave}
              className={`${CLASE_INPUT} px-11`}
              {...form.register('clave')}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-lg text-primary transition-colors duration-150 hover:bg-primary/10"
              onClick={() => setVerClave((v) => !v)}
              aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {verClave ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          {erroresCampo.clave && (
            <p className="text-xs font-medium text-destructive">{erroresCampo.clave.message}</p>
          )}
          {/* Casilla del diseño de referencia (misma acción que el ojito). */}
          <label className="flex w-fit cursor-pointer items-center gap-2 text-xs font-medium text-primary">
            <input
              type="checkbox"
              className="size-3.5 accent-primary"
              checked={verClave}
              onChange={(e) => setVerClave(e.target.checked)}
            />
            Mostrar contraseña
          </label>
        </motion.div>

        {error && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="rounded-xl bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
          >
            {error}
          </motion.p>
        )}

        <motion.div {...campo(0.61)} className="flex justify-center">
          <Button
            type="submit"
            size="lg"
            className="h-12 w-full rounded-xl bg-gradient-to-r from-[#15803d] to-[#166534] text-[15px] font-semibold text-white shadow-lg shadow-primary/25 transition-all duration-200 hover:from-[#166534] hover:to-[#14532d] hover:shadow-xl active:scale-[0.99] sm:w-auto sm:min-w-52"
            disabled={ocupado}
          >
            {estado === 'ok' ? (
              <CircleCheck className="size-4" />
            ) : ocupado ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <LogIn className="size-4" />
            )}
            {TEXTO_ESTADO[estado]}
          </Button>
        </motion.div>
      </form>
    </>
  );
}
